import React, { useState, useContext } from "react";
import { Bot, Sparkles, Send, Loader2 } from "lucide-react";
import models from "shared/ai_models.json";
import { AuthContext } from "../Auth/AuthProvider.js";
import { backend_url } from "shared/config.js";

const AGENT_OPTIONS = models.models.reduce((unique, model) => {
  const exists = unique.find((item) => item.value === model.familyName);
  if (!exists) {
    unique.push({
      value: model.familyName,
      label: `${model.company} ${model.familyName}`,
    });
  }
  return unique;
}, []);

const FORM_TYPES = {
  COMMENT: "comment",
  REPLY: "reply",
  ANSWER: "answer",
  EDIT: "edit",
  QUESTION: "question",
};

// Convert form type strings to numeric types for PostSchema
const getNumericType = (formType) => {
  switch (formType) {
    case FORM_TYPES.QUESTION:
      return 1;
    case FORM_TYPES.ANSWER:
      return 2; // Answer to a question
    case FORM_TYPES.COMMENT:
    case FORM_TYPES.REPLY:
      return 3; // Comment to an answer
    default:
      return 0; // Unknown
  }
};

/**
 * Unified CreatePostForm Component
 *
 * This component handles all types of form responses including:
 * - Comments on posts/answers
 * - Replies to comments
 * - Answers to questions
 * - Editing existing content
 *
 * Behavior is implicit based on what user selects:
 * - Text + Agents selected = User content + AI enhancement
 * - Text only = User content only
 * - Agents only = AI-generated content only
 *
 * CALLBACK PATTERN:
 * Parent components provide an onSubmit callback that receives the form data.
 *
 * @param {Object} props
 * @param {Function} props.onSubmit - Callback to handle form submission (REQUIRED)
 * @param {Function} props.onCancel - Callback to handle form cancellation
 * @param {Function} props.onSuccess - Callback after successful submission
 * @param {Object} props.user - Current user object
 * @param {string} props.formType - Type of form (comment|reply|answer|edit)
 * @param {string} props.parentId - ID of parent post/comment for replies
 * @param {string} props.initialContent - Initial content for edit mode
 * @param {string} props.placeholder - Custom placeholder text
 * @param {boolean} props.showAdvanced - Show AI enhancement options
 * @param {boolean} props.isTopLevel - Whether this is a top-level form
 */
const CreatePostForm = ({
  // Core callbacks - THESE ARE THE KEY FOR PARENT COMMUNICATION
  onSubmit, // Parent MUST provide this to handle the response
  onCancel, // Optional: called when user cancels
  onSuccess, // Optional: called after successful submission

  //  context
  formType = null, // Type of form (comment|reply|answer)
  parentId = null,

  // Content and UI
  initialContent = "",
  placeholder,
  showAdvanced = true,
  isTopLevel = false,

  // Additional options
  maxLength = 1000,

  // Style customization
  className = "",
  buttonText,
}) => {
  const { user, userInfo, accessToken } = useContext(AuthContext);
  const [content, setContent] = useState(initialContent);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [isAnonymous, setIsAnonymous] = useState(false);

  // Simple validation - need either content or agents
  const hasContent = content.trim().length > 0;
  const hasAgents = selectedAgents.length > 0;

  const getPlaceholderText = () => {
    if (placeholder) return placeholder;

    switch (formType) {
      case FORM_TYPES.ANSWER:
        return "Share your answer... (Markdown supported)";
      case FORM_TYPES.REPLY:
        return "Write a thoughtful reply...";
      case FORM_TYPES.EDIT:
        return "Edit your content...";
      default:
        return "Add to the discussion...";
    }
  };

  const getButtonText = () => {
    if (buttonText) return buttonText;

    // Determine button text based on content and AI agent selection
    const getActionText = () => {
      if (hasContent && hasAgents) {
        // Both text and AI agents selected
        switch (formType) {
          case FORM_TYPES.ANSWER:
            return "Post Answer + AI";
          case FORM_TYPES.REPLY:
            return "Post Reply + AI";
          case FORM_TYPES.EDIT:
            return "Save Changes + AI";
          default:
            return "Post Comment + AI";
        }
      } else if (hasAgents && !hasContent) {
        // Only AI agents selected, no text
        switch (formType) {
          case FORM_TYPES.ANSWER:
            return "AI Answer";
          case FORM_TYPES.REPLY:
            return "AI Reply";
          case FORM_TYPES.EDIT:
            return "AI Edit";
          default:
            return "AI Comment";
        }
      } else {
        // Only text content, no AI agents (or neither selected)
        switch (formType) {
          case FORM_TYPES.ANSWER:
            return "Post Answer";
          case FORM_TYPES.REPLY:
            return "Post Reply";
          case FORM_TYPES.EDIT:
            return "Save Changes";
          default:
            return "Post Comment";
        }
      }
    };

    // Add loading state suffix
    if (isSubmitting) {
      const baseText = getActionText();
      if (baseText.includes("AI")) {
        return baseText.replace("AI", "Generating AI");
      } else {
        return baseText.replace("Post", "Posting").replace("Save", "Saving");
      }
    }

    return getActionText();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !onSubmit) return;

    // Validate that we have either content or agents selected
    if (!hasContent && !hasAgents) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare submission data to pass to parent
      const submissionData = {
        uid: user.uid,

        post: {
          uid: user.uid, // Use user ID if available
          content: content.trim(),
          parent: parentId,
          type: getNumericType(formType),
          isAnonymous: isAnonymous,
        },
        // AI enhancement options
        selectedAgents: hasAgents ? selectedAgents : [],
      };

      // Call the parent's onSubmit callback - THIS IS THE KEY!
      const result = await onSubmit(submissionData);

      // Reset form after successful submission
      if (result !== false) {
        // Allow parent to prevent reset by returning false
        setContent("");
        setSelectedAgents([]);
        setIsAnonymous(false);

        // Call success callback if provided
        if (onSuccess) {
          onSuccess(result);
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      // Don't reset form on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setContent(initialContent);
    setSelectedAgents([]);
    setIsAnonymous(false);

    if (onCancel) {
      onCancel();
    }
  };

  const toggleAgentSelection = (agentValue) => {
    setSelectedAgents((prev) => {
      if (prev.includes(agentValue)) {
        return prev.filter((agent) => agent !== agentValue);
      } else {
        return [...prev, agentValue];
      }
    });
  };

  return (
    <div className={`${className} relative`}>
      {/* Loading overlay */}
      {isSubmitting && (
        <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-20 rounded-xl">
          <div className="flex flex-col items-center space-y-3">
            <div className="relative">
              <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
              <div className="absolute inset-0 w-10 h-10 border-2 border-blue-200 rounded-full opacity-25"></div>
            </div>
            <div className="text-center">
              <span className="text-sm font-semibold text-gray-800">
                {hasAgents ? "Generating AI Response..." : "Publishing..."}
              </span>
              <div className="flex space-x-1 mt-1">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce"></div>
                <div
                  className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md">
        <form onSubmit={handleSubmit} className="space-y-0">
          {/* Main Content Container */}
          <div className="relative">
            {/* Content Input Area */}
            <div className="p-4 pb-0">
              <div className="flex space-x-3">
                {/* User Avatar */}
                {userInfo?.profilePicture && (
                  <div className="flex-shrink-0">
                    <img
                      src={userInfo.profilePicture}
                      alt="Your avatar"
                      className="w-10 h-10 rounded-full object-cover border-2 border-gray-100 shadow-sm"
                      onError={(e) => {
                        e.target.src = `${backend_url}/default_profile_pic2.jpeg`;
                      }}
                    />
                  </div>
                )}

                {/* Text Input */}
                <div className="flex-1 min-w-0">
                  <div className="relative">
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      rows={Math.max(
                        3,
                        Math.min(8, content.split("\n").length)
                      )}
                      className="w-full p-4 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white outline-none resize-none placeholder-gray-500 transition-all duration-200"
                      placeholder={getPlaceholderText()}
                      maxLength={maxLength}
                      aria-label={`${formType} content`}
                    />

                    {/* Character Counter */}
                    <div className="absolute bottom-3 right-3">
                      <span
                        className={`text-xs font-medium ${
                          content.length > maxLength * 0.9
                            ? content.length >= maxLength
                              ? "text-red-500"
                              : "text-amber-500"
                            : "text-gray-400"
                        }`}
                      >
                        {content.length}/{maxLength}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Agent Selection */}
            {showAdvanced && (
              <div className="px-4 pb-4">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-4 mt-3">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="flex items-center justify-center w-6 h-6 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-800">
                      AI Enhancement
                    </span>
                    <div className="flex-1"></div>
                    {selectedAgents.length > 0 && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                        {selectedAgents.length} selected
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {AGENT_OPTIONS.map((agent) => (
                      <button
                        key={agent.value}
                        type="button"
                        onClick={() => toggleAgentSelection(agent.value)}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                          selectedAgents.includes(agent.value)
                            ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md transform scale-105"
                            : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-blue-200 hover:text-blue-600"
                        }`}
                      >
                        <Bot className="w-3.5 h-3.5" />
                        <span>{agent.label}</span>
                        {selectedAgents.includes(agent.value) && (
                          <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                        )}
                      </button>
                    ))}
                  </div>

                  {selectedAgents.length === 0 && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Select AI agents to enhance your {formType || "post"} with
                      intelligent responses
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            {/* Left Side - Options */}
            <div className="flex items-center space-x-4">
              {/* Anonymous Toggle */}
              <label className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer hover:text-gray-800 transition-colors duration-200">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={isAnonymous}
                    onChange={(e) => setIsAnonymous(e.target.checked)}
                    className="sr-only"
                  />
                  <div
                    className={`w-9 h-5 rounded-full transition-all duration-200 ${
                      isAnonymous ? "bg-blue-500" : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-all duration-200 ${
                        isAnonymous ? "translate-x-4" : "translate-x-0.5"
                      } mt-0.5`}
                    ></div>
                  </div>
                </div>
                <span className="font-medium">Anonymous</span>
              </label>

              {/* Cancel Button */}
              {!isTopLevel && onCancel && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
                >
                  Cancel
                </button>
              )}
            </div>

            {/* Right Side - Submit */}
            <div className="flex items-center space-x-3">
              {/* Submit Button */}
              <button
                type="submit"
                disabled={
                  isSubmitting || (!hasContent && !hasAgents) || !onSubmit
                }
                className={`flex items-center space-x-2 px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 active:scale-95 ${
                  hasAgents
                    ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg hover:shadow-xl"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg hover:shadow-xl"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{getButtonText()}</span>
                  </>
                ) : (
                  <>
                    {hasAgents ? (
                      <Sparkles className="w-4 h-4" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    <span>{getButtonText()}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Export form types for easy access
CreatePostForm.FORM_TYPES = FORM_TYPES;

export default CreatePostForm;
